'use client'

import { useQuery } from '@tanstack/react-query'

import { getQueryClient, QueryKey } from 'shared/api'
import { isNilOrEmpty, isNotNilOrEmpty } from 'shared/utils/ramda'

import { getDeliveryMethodsByAddressService } from '../api'
import { PostalCodeTypes } from '../types'

interface UseQueryDeliveryMethodProps {
  city: string
  postalCode: string
  token?: string
}

/**
 * Centralized postal code validation function for both TMS and Standard delivery
 * Returns both validation result and postal code data to avoid duplicate API calls
 */
export const validateAndCheckPostalCode = async (
  postalCode: string,
  city: string
): Promise<{
  isSupported: boolean
  data: PostalCodeTypes[] | null
  deliveryMethodId: string | null
}> => {
  if (isNilOrEmpty(postalCode) || isNilOrEmpty(city)) {
    return Promise.resolve({
      isSupported: false,
      data: null,
      deliveryMethodId: null
    })
  }

  const data = await queryFetchDeliveryMethods({
    postalCode,
    city
  })

  if (isNotNilOrEmpty(data) && data.length > 0) {
    return Promise.resolve({
      isSupported: true,
      data,
      deliveryMethodId: data[0]?.id || null
    })
  }

  return Promise.resolve({
    isSupported: false,
    data: null,
    deliveryMethodId: null
  })
}

export default function useQueryDeliveryMethods({
  city,
  postalCode,
  token
}: UseQueryDeliveryMethodProps) {
  return useQuery<PostalCodeTypes[]>({
    queryKey: [QueryKey.deliveryMethod, city, postalCode, token],
    queryFn: async ({ queryKey }) => {
      const [, _city, _postalCode, _token] = queryKey as [
        string,
        string,
        string,
        string | undefined
      ]

      const response = await getDeliveryMethodsByAddressService(
        _postalCode,
        _city,
        _token
      )

      return response
    },
    enabled: !!city && !!postalCode,
    staleTime: 60000
  })
}

export const queryFetchDeliveryMethods = async ({
  city,
  postalCode
}: UseQueryDeliveryMethodProps) => {
  return await getQueryClient().fetchQuery<PostalCodeTypes[]>({
    queryKey: [QueryKey.deliveryMethod, city, postalCode],
    queryFn: async () => {
      const response = await getDeliveryMethodsByAddressService(
        postalCode,
        city
      )

      return response
    }
  })
}
